import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { STUDENT_NAV } from "@/pages/student/StudentHome";
import { ADVISOR_NAV } from "@/pages/advisor/AdvisorHome";
import { EMPLOYER_NAV } from "@/pages/employer/EmployerHome";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { DOMAINS, SKILLS, LEVELS } from "@/lib/framework";
import { Card } from "@/components/ui/card";
import {
  Compass, GraduationCap, ListChecks, Lightbulb, Layers, Gauge, Target,
  AlertTriangle, MessageSquareText, BookOpen, ShieldCheck, ExternalLink, Info,
} from "lucide-react";

const navFor = (role) =>
  ({ student: STUDENT_NAV, advisor: ADVISOR_NAV, faculty: ADVISOR_NAV, employer: EMPLOYER_NAV, admin: ADMIN_NAV }[role] || STUDENT_NAV);

const LEVEL_DESC = {
  emerging: "Early evidence of skill development.",
  developing: "Growing consistency and application.",
  proficient: "Reliable and effective performance.",
  advanced: "Highly consistent application across contexts.",
};

const GETTING_STARTED = {
  student: {
    start: "My TSB",
    body: "Start on My TSB to see your Transferable Skills Benchmark, your strongest domains, and where to grow. From there, open Employment Report for a shareable summary, What-if & Careers to explore how your skills line up with different careers, Career Interests to take the interest assessment, and the AI Skills Coach for personalized guidance.",
  },
  advisor: {
    start: "My Students",
    body: "Start on My Students to view your caseload and drill into any student's profile. Use Skills Update to record per-skill observations for a student, the Learning Academy for role-specific courses, and TSB Certification to become certified in the framework.",
  },
  faculty: {
    start: "My Students",
    body: "Start on My Students to view your caseload and drill into any student's profile. Use Skills Update to record per-skill observations, the Learning Academy for faculty courses, and TSB Certification (which includes syllabus-to-skill mapping) to become certified.",
  },
  employer: {
    start: "Talent & 360",
    body: "Start on Talent & 360 to browse students who have opted in to share their profiles and to submit an Employer 360 review. Use the Job Board to post opportunities; student fit is shown against the skills you prioritize.",
  },
  admin: {
    start: "Analytics",
    body: "Start on Analytics for cohort insights. Use User Accounts to manage people, Registration Requests and Tasks to process access, Communications to manage email templates, Career Profiles for occupation governance, and the Audit Trail for a record of activity.",
  },
};

const ADMIN_DOCS = [
  { name: "System Guide & Architecture (PRD)", covers: "Product spec, architecture overview, and the full dated change log.", loc: "/app/memory/PRD.md", change: true },
  { name: "Backup, Recovery & Rollback Runbook", covers: "Recovery levers (application/deployment rollback vs. database restore), the source-of-truth map, and a secrets inventory (names only — no values).", loc: "/app/memory/BACKUP_AND_RECOVERY.md" },
  { name: "Owner Go-Live Checklist", covers: "Ordered owner actions to reach the Pre-Team-Access Baseline (GitHub save, production secrets, publish, database export).", loc: "/app/memory/OWNER_GO_LIVE_CHECKLIST.md" },
  { name: "Team Preview Guide", covers: "Reviewer onboarding: what GPA+ is, how to log in, demo accounts, a short tour, and known preview limitations.", loc: "/app/memory/TEAM_PREVIEW_GUIDE.md" },
  { name: "Change Record — Website Authorization", covers: "Roles, permissions, and the preview-safe authorization model.", loc: "/app/memory/CHANGE_RECORD_website_authorization.md", change: true },
  { name: "Change Record — Registration & Approval", covers: "The controlled registration, approval, and task workflow.", loc: "/app/memory/CHANGE_RECORD_registration_approval.md", change: true },
];

const Section = ({ id, icon: Icon, title, children }) => (
  <Card id={id} className="scroll-mt-24 p-6 card-elevate" data-testid={`help-section-${id}`}>
    <div className="mb-3 flex items-center gap-2.5 text-[#003057]">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#003057]/6"><Icon className="h-5 w-5" /></span>
      <h2 className="font-heading text-lg font-bold">{title}</h2>
    </div>
    <div className="space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
  </Card>
);

const TOC = [
  ["getting-started", "Getting Started"],
  ["what-is-gpa", "What Is GPA+?"],
  ["understanding-tsb", "Understanding Your TSB"],
  ["skills", "The 10 Transferable Skills"],
  ["levels", "Development Levels"],
  ["domains", "The Three Domains"],
  ["interpretation", "Scores & Responsible Interpretation"],
  ["feedback", "Providing Feedback"],
  ["early-access", "Early Access / Known Limitations"],
];

export default function HelpCenter() {
  const { user } = useAuth();
  const role = user?.real_role || user?.role || "student";
  const gs = GETTING_STARTED[role] || GETTING_STARTED.student;
  const isAdmin = role === "admin";

  return (
    <DashboardShell title="Help & Documentation" subtitle="Guidance for using GPA+ during the early-access preview" nav={navFor(user?.role)}>
      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        {/* Table of contents */}
        <nav className="hidden lg:block" data-testid="help-toc">
          <div className="sticky top-6 space-y-1">
            {TOC.map(([id, label]) => (
              <a key={id} href={`#${id}`} className="block rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-500 hover:bg-[#003057]/5 hover:text-[#003057]">{label}</a>
            ))}
            {isAdmin && (
              <a href="#admin-docs" className="block rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-500 hover:bg-[#003057]/5 hover:text-[#003057]">Administrator Documentation</a>
            )}
          </div>
        </nav>

        <div className="space-y-6">
          <Section id="getting-started" icon={Compass} title="Getting Started with GPA+">
            <p>Welcome to GPA+. Your dashboard is organized around the menu on the left. As a <strong>{ {student:"Student",advisor:"Academic Advisor",faculty:"Faculty",employer:"Employer",admin:"Website Administrator"}[role] }</strong>, begin on <strong>{gs.start}</strong>.</p>
            <p>{gs.body}</p>
          </Section>

          <Section id="what-is-gpa" icon={BookOpen} title="What Is GPA+?">
            <p><strong>GPA+</strong> is the broader framework and initiative for making durable, transferable skills visible. The <strong>Transferable Skills Benchmark (TSB)</strong> is the measurement, scoring, and reporting component within GPA+.</p>
            <p>GPA+ <strong>complements</strong> the traditional GPA — it does not replace your transcript or grade point average. A GPA reflects academic content mastery; GPA+ describes what a learner can do with it across roles and contexts.</p>
            <p className="text-sm"><Link to="/framework" className="inline-flex items-center gap-1 font-semibold text-[#005291] hover:text-[#78be21]">Full framework explainer <ExternalLink className="h-3.5 w-3.5" /></Link> · <Link to="/faq" className="font-semibold text-[#005291] hover:text-[#78be21]">FAQ</Link></p>
          </Section>

          <Section id="understanding-tsb" icon={Gauge} title="Understanding Your TSB">
            <p>Your TSB summarizes your demonstrated transferable skills across the three domains into a single benchmark (shown on a 1.0–4.0 scale and a 0–100 view). It reflects <strong>evidence of what you have demonstrated</strong> — for example through coursework and, where applicable, advisor observations and opt-in employer input.</p>
            <p>Keep two ideas distinct: your <strong>demonstrated skills</strong> (your actual TSB profile) and <strong>career alignment</strong> (how that profile compares to what a given career tends to emphasize). Exploring careers never changes your demonstrated TSB.</p>
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900"><Info className="mr-1.5 inline h-4 w-4" />During this preview, the underlying calculations are still being audited and refined. Treat scores as indicative, not final.</p>
          </Section>

          <Section id="skills" icon={Layers} title="The 10 Transferable Skills">
            <p>GPA+ measures ten transferable skills, grouped into three domains:</p>
            <div className="grid gap-4 sm:grid-cols-3">
              {DOMAINS.map((d) => (
                <div key={d.id} className="rounded-xl border border-slate-200 p-4">
                  <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide" style={{ color: d.color }}>
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} />{d.name}
                  </p>
                  <ul className="space-y-1">
                    {SKILLS.filter((s) => s.domain === d.id).map((s) => (
                      <li key={s.id} className="text-sm font-semibold text-slate-700" data-testid={`help-skill-${s.id}`}>{s.name}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>

          <Section id="levels" icon={Target} title="Development Levels">
            <div className="space-y-2">
              {LEVELS.map((lv) => (
                <div key={lv.id} className="flex items-start gap-3" data-testid={`help-level-${lv.id}`}>
                  <span className="mt-0.5 inline-flex min-w-[92px] justify-center rounded-full border px-2.5 py-0.5 text-xs font-semibold" style={{ borderColor: lv.hex, color: lv.hex }}>{lv.name}</span>
                  <span>{LEVEL_DESC[lv.id]}</span>
                </div>
              ))}
            </div>
            <p className="rounded-lg bg-slate-100 px-3 py-2 text-slate-700"><strong>Insufficient Evidence is not the same as Emerging.</strong> When there is not yet enough evidence to describe a skill, that is a lack of data — not a low score. Emerging means early evidence <em>has</em> been observed.</p>
          </Section>

          <Section id="domains" icon={Layers} title="The Three GPA+ Domains">
            <p>The ten skills roll up into three domains. This is the currently approved mapping:</p>
            <div className="space-y-3">
              {DOMAINS.map((d) => (
                <div key={d.id}>
                  <p className="font-semibold" style={{ color: d.color }}>{d.name}</p>
                  <p className="text-sm text-slate-600">{SKILLS.filter((s) => s.domain === d.id).map((s) => s.name).join(" · ")}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="interpretation" icon={AlertTriangle} title="Scores, Career Information & Responsible Interpretation">
            <p>A student's <strong>demonstrated TSB profile</strong> and <strong>Career Alignment</strong> are different concepts. Selecting or exploring a career shows how your existing profile compares to that career's typical emphasis — it does <strong>not</strong> change your demonstrated TSB.</p>
            <p>During the MVP, calculations, career-alignment methodology, occupational mappings, and recommendations are still being audited and refined where applicable. Prototype results should not be relied upon for official academic, hiring, screening, promotion, or other consequential decisions.</p>
          </Section>

          <Section id="feedback" icon={MessageSquareText} title="Providing Feedback">
            <p>Your feedback shapes GPA+. Please use <Link to="/app/suggestions" className="font-semibold text-[#005291] hover:text-[#78be21]">Submit a Suggestion</Link> to tell us about:</p>
            <ul className="ml-5 list-disc space-y-1">
              <li>something that's confusing</li>
              <li>something that appears incorrect</li>
              <li>missing information</li>
              <li>an idea for improvement</li>
              <li>something you particularly like</li>
            </ul>
          </Section>

          <Section id="early-access" icon={AlertTriangle} title="Early Access / Known Limitations">
            <p className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
              GPA+ is currently an <strong>early-access prototype</strong>. Calculations, scoring methodologies, career-alignment methods, terminology, reports, and features are still being audited and refined. Content and functionality may change frequently during this period.
            </p>
            <p>Because it is a prototype, results should not currently be relied upon for official academic, hiring, screening, promotion, or other consequential decisions. Demo data does not represent real student records, and email delivery and some links are limited during the preview.</p>
          </Section>

          {isAdmin && (
            <Section id="admin-docs" icon={ShieldCheck} title="Website Administrator Documentation">
              <p>The following operational and developer documentation is maintained in the project repository under <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">/app/memory/</code> and is intentionally <strong>not</strong> rendered inside the application. This index tells you what exists, what each covers, and where it is maintained. Sensitive details (passwords, secrets, tokens, keys, connection strings) are never stored in documentation or exposed here.</p>
              <div className="mt-2 space-y-2">
                {ADMIN_DOCS.map((doc) => (
                  <div key={doc.name} className="rounded-lg border border-slate-200 p-3" data-testid="admin-doc-item">
                    <p className="flex items-center gap-2 font-semibold text-[#003057]">{doc.name}{doc.change && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">change log</span>}</p>
                    <p className="text-sm text-slate-600">{doc.covers}</p>
                    <p className="mt-0.5 text-xs font-mono-num text-slate-400">{doc.loc}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">Full operational procedures (backup, recovery, deployment, architecture) remain developer-only by design. Open decisions and detailed methodology notes are tracked in the PRD and change records above.</p>
            </Section>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
